import { useEffect, useState } from "react";
import api from "../../../../../Axios.js";
import { toast } from "react-toastify";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete
} from "@mui/material";

export default function Step1Basic({ programId, setProgramId, initialData, next }) {

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    learning_category_id: "",
    program_name: "",
    program_code: "",
    learning_mode: "online",
    description: ""
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setForm({
        learning_category_id: initialData.learning_category_id || "",
        program_name: initialData.program_name || "",
        program_code: initialData.program_code || "",
        learning_mode: initialData.learning_mode || "online",
        description: initialData.description || ""
      });
      if (initialData.id && !programId) setProgramId(initialData.id);
    }
  }, [initialData]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/lnd/get-category-dropdown");
      setCategories(res.data.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const validateForm = () => {

    const validPattern = /^[A-Za-z0-9 _-]+$/;
    let newErrors = {};
    if (!form.learning_category_id) newErrors.learning_category_id = "Learning Category is required";
    if (!form.program_name.trim()) newErrors.program_name = "Program Name is required";
    if (!form.program_code.trim()) newErrors.program_code = "Program Code is required";
    if (form.program_name.trim().length > 50) newErrors.program_name = "Program name max 50 characters";
    if (!validPattern.test(form.program_name.trim())) newErrors.program_name = "Only letters, numbers, hyphen (-) and underscore (_) are allowed";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveDraft = async (isNext) => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        learning_category_id: form.learning_category_id,
        program_name: form.program_name,
        program_code: form.program_code,
        learning_mode: form.learning_mode,
        description: form.description
      };

      let res;
      if (programId) {
        res = await api.put(`/lnd/${programId}`, payload);
      } else {
        res = await api.post("/lnd", payload);
      }

      const createdProgram = res.data?.data?.program;
      if (createdProgram?.id) setProgramId(createdProgram.id);

      toast.success(isNext ? "Your changes have been saved. Continue updating the details" : "Draft saved successfully");
      if (isNext) next();

    } catch (err) {
      console.log(err, "err in saving draft");
      toast.error(
        err?.response?.data?.message || "Failed to save draft"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>

      <div className="card shadow-sm border-0 mb-4 p-4 rounded-3">
        <h5 className="mb-4 fw-bold">Basic Details</h5>

        {/* CATEGORY */}
        <div className="mb-3">
          <Autocomplete
            options={categories || []}
            getOptionLabel={(option) => option.title || ""}

            // 🔥 map id → object (VERY IMPORTANT)
            value={
              categories.find((cat) => cat.id === form.learning_category_id) || null
            }

            onChange={(e, newValue) => {
              setForm({
                ...form,
                learning_category_id: newValue?.id || "",
              });

              if (errors.learning_category_id) {
                setErrors({
                  ...errors,
                  learning_category_id: "",
                });
              }
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                label="Learning Category *"
                size="small"
                error={!!errors.learning_category_id}
                helperText={errors.learning_category_id}
              />
            )}
          />
        </div>

        {/* PROGRAM NAME */}
        <TextField
          fullWidth
          size="small"
          label="Program Name *"
          placeholder="Enter program name"
          value={form.program_name}
          onChange={(e) => {
            setForm({ ...form, program_name: e.target.value });
            if (errors.program_name) setErrors({ ...errors, program_name: "" });
            if ((e.target.value.trim()).length > 50) setErrors({ ...errors, program_name: "Program name max 50 characters" });
          }}
          error={!!errors.program_name}
          helperText={errors.program_name}
          sx={{ mb: 3 }}
        />

        {/* PROGRAM CODE */}
        <TextField
          fullWidth
          size="small"
          label="Program Code *"
          placeholder="Enter program code"
          value={form.program_code}
          onChange={(e) => {
            setForm({ ...form, program_code: e.target.value });
            if (errors.program_code) setErrors({ ...errors, program_code: "" });
          }}
          error={!!errors.program_code}
          helperText={errors.program_code || "Hint: Unique Id (e.g. PRG-100)"}
          sx={{ mb: 3 }}
        />

        {/* LEARNING MODE */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>
            Learning Mode *
          </InputLabel>
          <Select
            value={form.learning_mode}
            label="Learning Mode *"
            onChange={(e) =>
              setForm({ ...form, learning_mode: e.target.value })
            }
          >
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </Select>
        </FormControl>

        {/* DESCRIPTION */}
        <TextField
          fullWidth
          size="small"
          label="Description"
          multiline
          rows={3}
          placeholder="Enter program description"
          value={form.description}
          onChange={(e) =>
            setForm({ ...form, description: e.target.value })
          }
          sx={{ mb: 4 }}
        />
      </div>

      {/* BUTTONS */}
      <div className="d-flex justify-content-end gap-3 px-2">
        <button
          className="btn btn-secondary px-4 fw-medium"
          disabled={loading}
          onClick={() => saveDraft(0)}
        >
          {loading ? "Saving..." : "Save as Draft"}
        </button>

        <button
          className="btn btn-success px-4 fw-medium"
          disabled={loading}
          onClick={() => saveDraft(1)}
        >
          Save & Continue
        </button>
      </div>

    </div>
  );
}